<div class="flex-1 flex flex-col overflow-hidden">
  <!-- Upload Content -->
  <main class="flex-1 overflow-y-auto p-6">
    <!-- Back Navigation and Title -->
    <div class="flex justify-between items-baseline mb-3">
      <button (click)="cancel()" class="flex items-center">
        <i class="pi pi-arrow-left me-3"></i>
        Upload New Document
      </button>

      <!-- Step 2: Document Details + Step Indicator -->
      <div class="mb-6 flex items-center justify-end gap-3">
        @if (currentStep === 2) {
        <div
          class="relative"
          (mouseenter)="showDocumentDetailsPopover = true"
          (mouseleave)="showDocumentDetailsPopover = false"
        >
          <button
            type="button"
            class="bg-white border border-dashed border-gray-300 px-4 py-2 rounded-lg text-sm font-medium text-gray-800 hover:border-gray-400 transition-colors"
          >
            Document Details
          </button>
          @if (showDocumentDetailsPopover) {
          <div
            class="absolute right-0 top-full mt-2 z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-2"
          >
            <div class="flex items-start gap-2 text-sm">
              <span class="font-medium text-gray-700 whitespace-nowrap">Document Name:</span>
              <span class="text-gray-900 break-words">{{ step1Form.get("documentName")?.value || "—" }}</span>
            </div>
            <div class="flex items-start gap-2 text-sm">
              <span class="font-medium text-gray-700 whitespace-nowrap">File Name:</span>
              <button
                type="button"
                (click)="onViewDocument(); showDocumentDetailsPopover = false"
                class="text-sm font-medium underline cursor-pointer hover:opacity-80 text-left"
                style="color: var(--color-brand-success)"
              >
                {{ uploadedFiles.length > 0 ? uploadedFiles[0].name : "—" }}
              </button>
            </div>
            <div class="flex items-start gap-2 text-sm">
              <span class="font-medium text-gray-700 whitespace-nowrap">Due Date:</span>
              <span class="text-gray-900">
                {{ step1Form.get("dueDate")?.value ? (step1Form.get("dueDate")?.value | date : "dd/MM/yyyy") : "—" }}
              </span>
            </div>
          </div>
          }
        </div>
        }
        <!-- Step Indicator -->
        <div
          class="bg-white border border-dashed border-gray-300 px-4 py-2 rounded-lg"
        >
          <span class="text-gray-800 text-sm font-medium">
            Step:
            <span class="text-purple-600">{{
              currentStep.toString().padStart(2, "0")
            }}</span
            >/<span class="text-gray-800">{{
              totalSteps.toString().padStart(2, "0")
            }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Left Side - Form (full width when step 2) -->
      <div
        class="space-y-6"
        [class.lg:col-span-2]="currentStep === 2"
      >
        <!-- Step 1: Document Upload -->
        @if (currentStep === 1) {
        <form [formGroup]="step1Form" class="space-y-6">
          <!-- Document Name -->
          <div>
            <label
              for="documentName"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Document Name
            </label>
            <input
              type="text"
              id="documentName"
              formControlName="documentName"
              placeholder="Enter document name"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              [class.border-red-500]="hasFieldError(step1Form, 'documentName')"
              (blur)="step1Form.get('documentName')?.markAsTouched()"
            />
            @if (hasFieldError(step1Form, 'documentName')) {
            <div class="text-red-500 text-sm mt-1">
              {{ getFieldError(step1Form, "documentName") }}
            </div>
            }
          </div>

          <!-- Due Date -->
          <div>
            <label
              for="dueDate"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Due Date
            </label>
            <div class="">
              <p-datepicker
                id="dueDate"
                formControlName="dueDate"
                appendTo="body"
                [minDate]="minDate"
                [showIcon]="true"
                [showButtonBar]="true"
                dateFormat="dd/mm/yy"
                placeholder="Select due date"
                [class.border-red-500]="hasFieldError(step1Form, 'dueDate')"
                class="w-full"
                [readonlyInput]="false"
                [showOnFocus]="true"
                (onSelect)="step1Form.get('dueDate')?.markAsTouched()"
                (onClear)="step1Form.get('dueDate')?.markAsTouched()"
              ></p-datepicker>
            </div>
            @if (hasFieldError(step1Form, 'dueDate')) {
            <div class="text-red-500 text-sm mt-1">
              {{ getFieldError(step1Form, "dueDate") }}
            </div>
            }
          </div>
        </form>
        }

        <!-- Step 2: User Assignment (levels stacked; users side-by-side) -->
        @if (currentStep === 2) {
        <form [formGroup]="step2Form" class="space-y-6">
          <div formArrayName="levels" class="space-y-6">
            @for (levelGroup of levelsFormArray.controls; track $index; let levelIndex = $index) {
            <div
              [formGroupName]="levelIndex"
              class="p-4 border border-gray-200 rounded-lg bg-gray-50 flex flex-col"
            >
              <!-- Level header: Level <n> - Permission dropdown (25% width) -->
              <div class="flex items-start justify-between gap-3 mb-3">
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <h4 class="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Level : {{ (levelIndex + 1) }}
                  </h4>
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span class="text-sm font-medium text-gray-700 whitespace-nowrap">
                      - Permission :
                    </span>
                    <div class="w-75">
                      <p-select
                        formControlName="permission"
                        [options]="permissionOptions"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="isLoadingPermissions ? 'Loading...' : 'Select Permission'"
                        [disabled]="isLoadingPermissions"
                        [class.border-red-500]="levelGroup.get('permission')?.invalid && levelGroup.get('permission')?.touched"
                        class="w-full permission-compact"
                        appendTo="body"
                        [showClear]="false"
                      ></p-select>
                    </div>
                  </div>
                </div>
                <div class="shrink-0">
                  @if (levelsFormArray.length > 1 && levelIndex > 0) {
                  <button
                    type="button"
                    (click)="removeLevel(levelIndex)"
                    class="text-red-500 hover:text-red-700 text-sm p-0.5"
                    title="Remove level"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                  }
                </div>
              </div>
              @if (levelGroup.get('permission')?.invalid && levelGroup.get('permission')?.touched) {
              <div class="text-red-500 text-sm -mt-2 mb-3">Permission is required</div>
              }

              <!-- Users in this level (side-by-side, add with +) -->
              <div formArrayName="users" class="flex-1">
                <div class="flex flex-wrap gap-3">
                @for (userControl of getLevelUsers(levelIndex).controls; track $index; let userIndex = $index) {
                <div
                  [formGroupName]="userIndex"
                  class="relative min-w-0 basis-full sm:basis-[calc(50%-0.75rem)] md:basis-[calc(33.333%-0.75rem)] lg:basis-[calc(16.666%-0.75rem)]"
                >
                  @if (getLevelUsers(levelIndex).length > 1 && userIndex > 0) {
                  <button
                    type="button"
                    (click)="removeUserFromLevel(levelIndex, userIndex)"
                    class="absolute -top-2 -right-2 z-10 bg-white border border-gray-200 rounded-full p-1 text-red-500 hover:text-red-700"
                    title="Remove user"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                  }
                    <p-select
                      formControlName="userId"
                      [options]="getFilteredUserOptions(levelIndex, userIndex)"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="isLoadingUsers ? 'Loading...' : 'Select User'"
                      [disabled]="isLoadingUsers"
                      [class.border-red-500]="(getLevelUsers(levelIndex).at(userIndex).get('userId'))?.invalid && (getLevelUsers(levelIndex).at(userIndex).get('userId'))?.touched"
                      styleClass="w-full"
                      appendTo="body"
                      [showClear]="false"
                    ></p-select>
                    @if (getLevelUsers(levelIndex).at(userIndex).get('userId')?.invalid && getLevelUsers(levelIndex).at(userIndex).get('userId')?.touched) {
                    <div class="text-red-500 text-sm mt-1">User is required</div>
                    }
                </div>
                }
                <button
                  type="button"
                  (click)="addUserToLevel(levelIndex)"
                  class="h-[42px] px-3 border rounded-lg text-sm font-medium transition-colors flex items-center justify-center w-25"
                  style="border-color: var(--color-table-header); background: none; color: inherit"
                  title="Add user"
                >
                  + Add User
                </button>
                </div>
              </div>
            </div>
            }
          </div>

          <!-- Add Level -->
          <div class="pt-2">
            <button
              type="button"
              (click)="addLevel()"
              class="flex items-center px-4 py-2 text-sm font-medium border rounded-lg transition-colors"
              style="border-color: var(--color-table-header); background: none; color: inherit"
            >
              + Add Level
            </button>
          </div>
        </form>
        }
      </div>

      <!-- Right Side - Upload Area (Only for Step 1) -->
      @if (currentStep === 1) {
      <div class="flex flex-col">
        <div
          class="flex-1 border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center transition-colors"
          [class.border-teal-500]="isDragOver && !hasFieldError(step1Form, 'uploadedFile')"
          [class.bg-teal-50]="isDragOver && !hasFieldError(step1Form, 'uploadedFile')"
          [class.border-gray-300]="!isDragOver && !hasFieldError(step1Form, 'uploadedFile')"
          [class.border-red-500]="hasFieldError(step1Form, 'uploadedFile')"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
        >
          <!-- Upload Icon -->
          <svg
            class="w-12 h-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            ></path>
          </svg>

          <!-- Upload Text -->
          <div class="text-center">
            <p class="text-lg font-medium text-gray-900 mb-2">
              Upload Document
            </p>
            <p class="text-sm text-gray-500 mb-4">
              Drag and drop your file here, or click to browse
            </p>

            <!-- File Input -->
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              (change)="onFileSelected($event)"
              class="hidden"
              #fileInput
            />
            <button
              type="button"
              (click)="fileInput.click()"
              class="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Choose File
            </button>
          </div>

          <!-- Selected File Display -->
          @if (uploadedFiles.length > 0) {
          <div
            class="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg w-full"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <svg
                  class="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                <span class="text-sm text-green-700">{{
                  uploadedFiles[0].name
                }}</span>
              </div>
              <button
                type="button"
                (click)="onFileRemove()"
                class="text-red-500 hover:text-red-700"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
          }

          <!-- Error Message -->
          @if (hasFieldError(step1Form, 'uploadedFile')) {
          <div class="mt-2 text-red-500 text-sm">
            {{ getFieldError(step1Form, "uploadedFile") }}
          </div>
          }
        </div>
      </div>
      }

    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-4 mt-8">
      <!-- <button
          type="button"
          (click)="cancel()"
          class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button> -->

      @if (currentStep < totalSteps) {
      <button
        type="button"
        (click)="cancel()"
        class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="nextStep()"
        class="px-6 py-2 text-white rounded-lg transition-colors next-button cursor-pointer"
        [disabled]="!canProceedToStep2"
        [style.opacity]="canProceedToStep2 ? '1' : '0.5'"
        [style.cursor]="canProceedToStep2 ? 'pointer' : 'not-allowed'"
      >
        Next
      </button>
      } @if (currentStep === 2) {
      <button
        type="button"
        (click)="previousStep()"
        class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      } @if (currentStep === totalSteps) {
      <button
        type="button"
        (click)="submitForm()"
        class="px-6 py-2 text-white rounded-lg transition-colors cursor-pointer"
        style="background: var(--color-table-header); color: white"
        [disabled]="!canSubmit || loading"
      >
        @if (loading) {
        <span
          class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
        ></span>
        }
        {{ loading ? "Creating..." : "Submit" }}
      </button>
      }
    </div>
  </main>
  <!-- Document Viewer Modal -->
  <app-document-viewer-modal
    [(visible)]="showDocumentViewer"
    [attachment]="currentDocument"
    (visibleChange)="showDocumentViewer = $event"
    (download)="onDocumentDownload($event)"
    (openInNewTab)="onDocumentOpenInNewTab($event)"
    (close)="onDocumentViewerClose()"
  ></app-document-viewer-modal>

  <!-- Toast Messages -->
  <p-toast></p-toast>
</div>
